import { useRef, useState, type JSX } from 'react'
import {
  customRangeProblem,
  monthLabel,
  monthSpanLabel,
  noDataMonths,
  rangeLabel,
  type CustomRange,
  type DataWindow,
  type PresetKind,
  type WindowKind,
} from '../timeWindow'

// The page-wide time window: current fiscal year, the next 18 months, or a
// custom range. It sits in its own row above the tabs because it governs every
// chart below, and it states the range it resolved to so "current fiscal year"
// is never ambiguous.
//
// `checked` is the reader's latest applied choice while `window` is what the
// numbers on screen were fetched for: the radio moves the instant it is clicked,
// and the range text catches up when the data lands, so the two never disagree
// about what is drawn.
//
// Custom opens a tray of month and year dropdowns, joined under the control so
// it reads as part of Custom, instead of changing anything: the charts change
// only on "Show range", so a half-picked range never loads. The tray counts the
// drafted months as they are picked, and the range text says "Showing" so it is
// never mistaken for the draft. The range sits on the switch row, so opening the
// tray never moves it. The tray closes without applying from its × or Esc: the
// edits are dropped and the switch returns to what is on screen.
// Clicking a preset closes the row, and calls `onChange` only when that preset
// is not already applied, so re-clicking what is on screen starts no load.
//
// The note appears only when the window runs past the last month the data
// covers; the charts then show where the data ends, and this says it once for
// the whole page.

const PRESETS: { kind: PresetKind; label: string }[] = [
  { kind: 'fiscal-year', label: 'Current fiscal year' },
  { kind: 'rolling-18', label: '18 months' },
]

const MONTHS = Array.from({ length: 12 }, (_, i) => {
  const value = String(i + 1).padStart(2, '0')
  return { value, label: monthLabel(`2026-${value}-01`).slice(0, 3) }
})

/** Re-clicking the preset already applied only closes the dropdowns. */
export function presetClick(kind: PresetKind, checked: WindowKind): 'change' | 'close-only' {
  return kind === checked ? 'close-only' : 'change'
}

/** Esc closes the custom tray without applying. */
export function closesPanel(key: string): boolean {
  return key === 'Escape'
}

/** Prefill for the dropdowns: the last applied custom range, else the window on screen. */
export function initialDraft(custom: CustomRange | null, window: DataWindow): CustomRange {
  return custom ?? { from: window.from, to: window.to }
}

/** The offered years, stretched to reach the draft's so the dropdown never shows a year it isn't. */
export function yearsCovering(years: number[], draft: CustomRange): number[] {
  const first = years[0]
  if (first === undefined) return years
  const last = Math.max(years[years.length - 1] ?? first, yearOf(draft.from), yearOf(draft.to))
  return Array.from({ length: last - first + 1 }, (_, i) => first + i)
}

function yearOf(month: string): number {
  return Number(month.slice(0, 4))
}

function MonthYear({
  caption,
  label,
  month,
  years,
  disabled,
  onPick,
}: {
  caption: string
  label: string
  month: string
  years: number[]
  disabled: boolean
  onPick: (month: string) => void
}): JSX.Element {
  const year = month.slice(0, 4)
  const mm = month.slice(5, 7)
  return (
    <div className="window-date">
      <span className="window-date-label" aria-hidden="true">
        {caption}
      </span>
      <span className="window-date-field">
        <select
          className="window-select"
          aria-label={`${label} month`}
          value={mm}
          disabled={disabled}
          onChange={(e) => onPick(`${year}-${e.target.value}-01`)}
        >
          {MONTHS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
        <select
          className="window-select"
          aria-label={`${label} year`}
          value={year}
          disabled={disabled}
          onChange={(e) => onPick(`${e.target.value}-${mm}-01`)}
        >
          {years.map((y) => (
            <option key={y} value={String(y)}>
              {y}
            </option>
          ))}
        </select>
      </span>
    </div>
  )
}

function CloseIcon(): JSX.Element {
  return (
    <svg className="window-close-icon" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function WarningIcon(): JSX.Element {
  return (
    <svg className="window-problem-icon" viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M8 1.75 15 14.25H1L8 1.75Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M8 6.25v3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="8" cy="11.9" r="0.85" fill="currentColor" />
    </svg>
  )
}

export function WindowPicker({
  window,
  checked,
  onChange,
  onApplyCustom,
  custom,
  years,
  disabled,
  dataEnd,
}: {
  window: DataWindow
  checked: WindowKind
  /** Presets only; Custom applies through `onApplyCustom`. */
  onChange: (kind: PresetKind) => void
  onApplyCustom: (range: CustomRange) => void
  /** The remembered custom range, used to prefill the dropdowns. */
  custom: CustomRange | null
  years: number[]
  disabled: boolean
  /** Last month the data covers; null means there is nothing to say. */
  dataEnd: string | null
}): JSX.Element {
  const [customOpen, setCustomOpen] = useState(checked === 'custom')
  const [draft, setDraft] = useState<CustomRange>(initialDraft(custom, window))
  // Closing hands focus back to the option on screen, so the keyboard user lands
  // where the switch now points instead of on <body>.
  const radios = useRef<Partial<Record<WindowKind, HTMLInputElement | null>>>({})
  const shown: WindowKind = customOpen ? 'custom' : checked
  const problem = customRangeProblem(draft)
  const yearOptions = yearsCovering(years, draft)
  const gap = dataEnd === null ? [] : noDataMonths(dataEnd, window)
  const first = gap[0]
  const optionClass = (kind: WindowKind): string =>
    kind === shown ? 'window-option checked' : 'window-option'
  const openCustom = (): void => {
    if (customOpen) return
    setDraft(initialDraft(custom, window))
    setCustomOpen(true)
  }
  const closeCustom = (): void => {
    setCustomOpen(false)
    radios.current[checked]?.focus()
  }
  return (
    <div className="window-picker">
      <fieldset className="window-fieldset">
        <legend className="window-legend">Time window</legend>
        <div
          className={customOpen ? 'window-stack is-open' : 'window-stack'}
          onKeyDown={(e) => {
            if (customOpen && closesPanel(e.key)) closeCustom()
          }}
        >
          <div className="window-bar">
            <div className="window-options">
              {PRESETS.map((option) => (
                <label key={option.kind} className={optionClass(option.kind)}>
                  <input
                    ref={(el) => {
                      radios.current[option.kind] = el
                    }}
                    type="radio"
                    name="time-window"
                    value={option.kind}
                    checked={option.kind === shown}
                    disabled={disabled}
                    onChange={() => {
                      setCustomOpen(false)
                      if (presetClick(option.kind, checked) === 'change') onChange(option.kind)
                    }}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
              <label className={optionClass('custom')}>
                {/* onClick as well as onChange: with a custom range applied and the
                  tray closed, Custom is already checked, and clicking a checked
                  radio fires no change, so the tray could never reopen. */}
                <input
                  ref={(el) => {
                    radios.current.custom = el
                  }}
                  type="radio"
                  name="time-window"
                  value="custom"
                  checked={shown === 'custom'}
                  disabled={disabled}
                  onChange={openCustom}
                  onClick={openCustom}
                />
                <span>Custom</span>
              </label>
            </div>
            <span className="window-range">
              <span className="window-range-label">Showing</span>{' '}
              {rangeLabel(window.from, window.to)}
            </span>
          </div>
          {customOpen && (
            <div className="window-custom" role="group" aria-label="Custom range">
              <button
                type="button"
                className="window-close"
                aria-label="Close custom range"
                title="Close"
                onClick={closeCustom}
              >
                <CloseIcon />
              </button>
              <MonthYear
                caption="From"
                label="Start"
                month={draft.from}
                years={yearOptions}
                disabled={disabled}
                onPick={(from) => setDraft({ ...draft, from })}
              />
              <span className="window-arrow" aria-hidden="true">
                →
              </span>
              <MonthYear
                caption="To"
                label="End"
                month={draft.to}
                years={yearOptions}
                disabled={disabled}
                onPick={(to) => setDraft({ ...draft, to })}
              />
              <button
                type="button"
                className="primary window-apply"
                disabled={disabled || problem !== null}
                onClick={() => onApplyCustom(draft)}
              >
                Show range
              </button>
              <div className="window-custom-foot">
                {problem === null && <span className="window-count">{monthSpanLabel(draft)}</span>}
                <p className="window-problem" role="status">
                  {problem !== null && (
                    <>
                      <WarningIcon />
                      {problem}
                    </>
                  )}
                </p>
              </div>
            </div>
          )}
        </div>
      </fieldset>
      {dataEnd !== null && first !== undefined && (
        <p className="window-note" role="note">
          The data runs to {monthLabel(dataEnd)}, so {rangeLabel(first, window.to)} shows no
          figures.
        </p>
      )}
    </div>
  )
}
